/*
 * Dashboards
 *
 * - Holds the current dashboard
 * - Handles everything about dashboards
 */

import { computed, ref, toRaw } from 'vue'
import { defineStore } from 'pinia'
import { useSettingsStore } from '@/stores/Settings'
import { useRealtimeStore } from '@/stores/Realtime'
import { useApiStore } from '@/stores/Api'

import globalEvents from '@/utilities/GlobalEvents'
import logger from '@/utilities/Logger'
import _i18n from '@/utilities/I18n'

import type { AxiosError } from 'axios'
import { PbxAppDashboardItemQueueStats, PbxAppDashboardItemType, PbxAppDashboardItemWebClose, PbxAppDashboardItemWebMode, type PbxAppDashboardResponse, type PbxAppDashboardItemCommon, type PbxAppDashboardItemExtensions, type PbxAppDashboardItemMyCalls, type PbxAppDashboardItemQueue, type PbxAppDashboardItemMultiQueue, type PbxAppDashboardItemShortcuts, type PbxAppDashboardItemSwitchVar, type PbxAppDashboardItemWeb, type PbxAppDashboardListResponseItem, type PbxAppDashboardListResponse } from '@/TcxApi/PbxApp'
import { DashboardMode, type DashboardGroupItem, type DashboardTopbarItem } from './Dashboards.types'
import { dynamicSort, extendObject, randomString } from '@/utilities/Tools'
import { SectionType } from './Settings.types'
import { EmployeeAccessLevel } from '@/TcxApi/Employee'
import type { PbxGroupListResponse } from '@/TcxApi/PbxGroup'

export const useDashboardsStore = defineStore('dashboards', () => {
	logger.debug('Dashboards: Initializing store')

	const ss = useSettingsStore()
	const i18n = _i18n.global
	const realtime = useRealtimeStore()
	const api = useApiStore()

	const dashboards = ref<PbxAppDashboardListResponseItem[]>([])
	const dashboard = ref<PbxAppDashboardResponse | undefined>(undefined)
	const previousDashboard = ref<PbxAppDashboardResponse | undefined>(undefined)
	const pbxGroups = ref<DashboardGroupItem[]>([])
	const mode = ref<DashboardMode>(DashboardMode.NORMAL) // NORMAL, CREATE, EDIT, WALLBOARD

	const dragItem = ref<PbxAppDashboardItemCommon | undefined>(undefined)
	const dragItemHeight = ref<number | undefined>(undefined)
	const dragTarget = ref<PbxAppDashboardItemCommon | undefined>(undefined)

	const ready = ref(false)

	globalEvents.on('Realtime:Connected', init)
	globalEvents.on('Auth:LoggedOut', () => {
		dashboards.value = []
		dashboard.value = undefined
		mode.value = DashboardMode.NORMAL
		previousDashboard.value = undefined
	})
	globalEvents.on('StatusRequest', () => {
		globalEvents.emit('StatusReport', { key: 'dashboards', value: ready.value })
	})
	globalEvents.on('Dashboard:DeleteItem', emergencyDeleteItem)
	globalEvents.on('Dashboard:Select', _id => { select(_id) })
	globalEvents.on('Dashboard:Delete', _id => { remove(_id) })

	if (realtime.connected) {
		init()
	}

	async function init() {
		await ss.ready()
		await loadList()
		await load()
		ready.value = true
	}

	// #region Data - load/save/delete dashboards

	const editMode = computed(() => { return mode.value !== DashboardMode.NORMAL && mode.value !== DashboardMode.WALLBOARD })
	const mayEdit = computed(() => { return ss.isDashboardEditor })

	/**
	 * Load the list of available dashboards
	 */
	async function loadList() {
		try {
			const res = await api.get<PbxAppDashboardListResponse>('/pbx/app/dashboard')

			res.data.sort(dynamicSort('name'))
			dashboards.value = res.data
		}
		catch (_) { /** Ignore - handled elsewhere */ }
	}

	/**
	 * Load the selected dashboard from server
	 */
	async function load() {
		// If dashboard id is invalid set it to first available dashboard - if any exists
		if (ss.settings.dashboardId && !dashboards.value.some(dashboard => dashboard._id === ss.settings.dashboardId)) {
			if (dashboards.value.length > 0) {
				ss.settings.dashboardId = dashboards.value[0]?._id as string
			} else {
				ss.settings.dashboardId = null
			}
		}

		// If no dashboard has been selected, select the first available if any exists
		if (!ss.settings.dashboardId && dashboards.value.length > 0) {
			ss.settings.dashboardId = dashboards.value[0]?._id as string
		}

		// If no dashboard available, ask the user if one should be auto-created
		if (!ss.settings.dashboardId) {
			globalEvents.emit('Dashboard:AutoCreate')
		}

		if (ss.settings.dashboardId) {
			try {
				const res = await api.get<PbxAppDashboardResponse>(`/pbx/app/dashboard/${ss.settings.dashboardId}`)

				for (const item of res.data.items) {
					if (!item.tag) {
						item.tag = randomString(64)
					}
				}

				dashboard.value = res.data
				if (ss.runtime.selectedSection === SectionType.DASHBOARD && dashboard.value && dashboard.value.topbar > 0) {
					ss.runtime.selectedSection = 'DASHBOARD-' + dashboard.value._id
				} else if (ss.runtime.selectedSection.startsWith('DASHBOARD-') && !dashboard.value?.topbar) {
					ss.runtime.selectedSection = SectionType.DASHBOARD
				}
			}
			catch (err) {
				if ((err as AxiosError).status === 404) {
					dashboard.value = undefined
					ss.settings.dashboardId = null
				}
			}
		}
	}

	/**
	 * This is inovoked by a queue if it is not able anymore to find the queue because nobody has access to it anymore
	 * - will remove the item, save it and reload the dashboard
	 * @param item Item to be deleted from the dashboard
	 */
	function emergencyDeleteItem(deleteItem: PbxAppDashboardItemCommon) {
		if (dashboard.value) {
			removeItem(deleteItem.column, deleteItem.position)
			const index = dashboard.value.items.findIndex(item => item.column === deleteItem.column && item.position === deleteItem.position)
			if (index !== -1) {
				dashboard.value.items.splice(index, 1)
				save()
			}
		}
	}

	/**
	 * Save the current dashboard
	 */
	async function save() {
		let error: string | undefined = undefined

		if (!dashboard.value) { return }

		for (const item of dashboard.value.items) {
			const recol = (str: string) => { return str.replace('%%column%%', (item.column + 1).toString()) }

			switch (item.type) {
				case PbxAppDashboardItemType.EXTENSIONS: {
					const extension = item as PbxAppDashboardItemExtensions
					if (!extension.title) { error = recol(i18n.t('dashboards.coworkerPanelWithoutName')) }
					if (extension.extensions.length === 0) { error = recol(i18n.t('dashboards.coworkerPanelHasNoContent')) }
					break
				}

				case PbxAppDashboardItemType.QUEUE: {
					const queue = item as PbxAppDashboardItemQueue
					if (!queue._id) { error = recol(i18n.t('dashboards.queuePanelWithQueue')) }
					if (!queue.variable) { queue.variableName = '' }
					break
				}

				case PbxAppDashboardItemType.WEB: {
					const web = item as PbxAppDashboardItemWeb
					if (web.width < 0 || web.width > 2000) { error = recol(i18n.t('dashboards.webPanelHasInvalidWidth')) }
					if (web.height < 0 || web.height > 2000) { error = recol(i18n.t('dashboards.webPanelHasInvalidHeight')) }
					if (!web.title) { error = recol(i18n.t('dashboards.webPanelWithoutName')) }
					if (!web.url) { error = recol(i18n.t('dashboards.webPanelWithoutUrl')) }
					break
				}

				case PbxAppDashboardItemType.SHORTCUTS: {
					const shortcuts = item as PbxAppDashboardItemShortcuts
					if (!shortcuts.title) { error = recol(i18n.t('dashboards.shortcutsPanelWithoutName')) }
					if (!shortcuts.shortcuts.every(sc => sc.name && sc.number)) { error = recol(i18n.t('dashboards.shortcutsPanelInvalidShortcuts')) }
					break
				}

				case PbxAppDashboardItemType.SWITCHVAR: {
					const switchvar = item as PbxAppDashboardItemSwitchVar
					if (!switchvar.title) { error = recol(i18n.t('dashboards.switchvarPanelWithoutName')) }
					if (!switchvar.variables.every(o => o.name && o.label)) { error = recol(i18n.t('dashboards.switchvarPanelInvalidSwitches')) }
					break
				}

				case PbxAppDashboardItemType.MYCALLS: {
					const myCalls = item as PbxAppDashboardItemMyCalls
					if (myCalls.variable && !myCalls.variableName) { myCalls.variable = '' }
					break
				}
			}
		}

		if (error) {
			const header = !dashboard.value._id ? i18n.t('dashboards.unexpectedErrorHeader') : i18n.t('dashboards.unexpectedUpdateErrorHeader')

			globalEvents.emit('CommonErrorModal', { header: header, message: `${i18n.t('dashboards.panelHasError')} ${error}` })
			return
		}

		if (!dashboard.value._id) { // Create
			if (dashboard.value.shared) {
				dashboard.value.employee = undefined
			} else {
				dashboard.value.employee = ss.auth?.employee
			}

			try {
				const res = await api.post('/pbx/app/dashboard', dashboard.value)

				for (const item of res.data.items) {
					if (!item.tag) {
						item.tag = randomString(64)
					}
				}
				dashboard.value = res.data

				mode.value = DashboardMode.NORMAL
				ss.settings.dashboardId = dashboard.value?._id ?? null

				await loadList()
			}
			catch (err) {
				globalEvents.emit('CommonErrorModal', {
					header: i18n.t('dashboards.unexpectedErrorHeader'),
					message: `${i18n.t('dashboards.unexpectedErrorMessage')} ${(err as AxiosError).message}`
				})
			}
		} else { // Update
			try {
				const res = await api.post(`/pbx/app/dashboard/${dashboard.value._id}`, dashboard.value)

				for (const item of res.data.items) {
					if (!item.tag) {
						item.tag = randomString(64)
					}
				}
				dashboard.value = res.data

				mode.value = DashboardMode.NORMAL
				await loadList()
			}
			catch (err) {
				globalEvents.emit('CommonErrorModal', {
					header: i18n.t('dashboards.unexpectedUpdateErrorHeader'),
					message: `${i18n.t('dashboards.unexpectedUpdateErrorMessage')} ${(err as AxiosError).message}`
				})
			}
		}
	}

	/**
	 * Delete a dashboard - and selects another if any
	 * @param _id Id of the dashboard
	 */
	async function remove(_id: string) {
		try {
			const name = dashboards.value.find(dashboard => dashboard._id === _id)?.name
			const isDeletingCurrent = _id === dashboard.value?._id

			try {
				await api.del(`/pbx/app/dashboard/${_id}`)
			}
			catch (err) {
				if ((err as AxiosError).status !== 404) { throw err }
			}

			mode.value = DashboardMode.NORMAL
			await loadList()

			if (isDeletingCurrent && dashboards.value.length > 0) {
				select(dashboards.value[0]?._id as string)
			} else {
				dashboard.value = undefined
			}

			const post = dashboard.value ? i18n.t('dashboards.deletedAndOtherSelected').replace('%%name%%', dashboard.value.name) : '.'
			const _ = new Notification(i18n.t('dashboards.deletedHeader'), {
				body: `'${name} ${i18n.t('dashboards.deletedMessage')}${post}`
			})
		}
		catch (err) {
			globalEvents.emit('CommonErrorModal', { header: i18n.t('dashboards.deleteFailedHeader'), message: `${i18n.t('dashboards.deleteFailedMessage')} ${(err as AxiosError).message}` })
		}
	}

	// #endregion

	// #region Mode Dashboard actions - select, create, edit, cancel

	/**
	 * Select a dashboard, and have it loaded
	 * @param id Id of dashboard
	 */
	function select(id: string) {
		ss.settings.dashboardId = id
		load()
	}

	/**
	 * Start creating a new dashboard
	 */
	function create() {
		if (dashboard.value) { previousDashboard.value = dashboard.value }
		dashboard.value = {
			_id: '',
			customer: ss.auth?.customer as string,
			name: '',
			topbar: 0,
			shared: false,
			group: undefined,
			items: []
		}
		mode.value = DashboardMode.CREATE

		if (ss.accessLevelIsMin(EmployeeAccessLevel.MANAGER)) {
			getGroupsForDashboardEdit().then(groups => { pbxGroups.value = groups })
		}
	}

	/**
	 * Start editing the current dashboard
	 */
	function edit() {
		if (dashboard.value?.shared && !ss.accessLevelIsMin(EmployeeAccessLevel.MANAGER)) {
			globalEvents.emit('CommonErrorModal', { header: i18n.t('dashboards.sharedEditAccessDeniedHeader'), message: i18n.t('dashboards.sharedEditAccessDeniedMessage') })
			return
		}

		if (dashboard.value) { previousDashboard.value = extendObject({}, toRaw(dashboard.value)) }
		mode.value = DashboardMode.EDIT

		if (dashboard.value?.shared) {
			getGroupsForDashboardEdit().then(groups => { pbxGroups.value = groups })
		}
	}

	/**
	 * Cancel create or edit mode, and return to previous dashboard if any
	 */
	function cancel() {
		dashboard.value = previousDashboard.value
		previousDashboard.value = undefined
		mode.value = DashboardMode.NORMAL
	}

	// #endregion

	// #region Columns

	/**
	 * Get the number of columns
	 */
	const columnCount = computed(() => {
		if (!dashboard.value) { return 0 }
		if (dashboard.value.items.length === 0) { return 1 }

		let highest = 0

		for (const item of dashboard.value.items) {
			if (item.column > highest) { highest = item.column }
		}
		return highest + 1
	})

	/**
	 * Return an array of column indexes - for containers layout of columns
	 */
	const columnIndexes = computed(() => {
		const cols = columnCount.value
		const list: number[] = []

		for (let index = 0; index < cols; index++) {
			list.push(index)
		}
		return list
	})

	/**
	 * Return the number items in the column
	 * @param column Index of column
	 * @returns Number of items in the column
	 */
	function getItemsInColumn(column: number) {
		if (!dashboard.value) { return 0 }
		return dashboard.value.items.filter(item => item.column === column).length
	}

	/**
	 * Return the items in a specific column
	 * @param column Index of column
	 * @returns List of items
	 */
	function getColumn(column: number) {
		if (!dashboard.value) { return [] }
		return dashboard.value.items.filter(item => item.column === column).sort(dynamicSort('position'))
	}

	/**
	 * Returns the width of the column, regular or web adjusted
	 * @param column Index of column
	 * @returns Width of column in pixels, with px suffix
	 */
	function getColumnWidth(column: number) {
		if (!dashboard.value) { return `${ss.settings.dashboardColumnWidth}px` }
		let width = ss.settings.dashboardColumnWidth

		dashboard.value.items.filter(item => item.column === column).forEach(item => {
			if (item.type === 'WEB') {
				if ((item as PbxAppDashboardItemWeb).width > width) { width = (item as PbxAppDashboardItemWeb).width }
			}
		})

		return `${width}px`
	}

	// #endregion

	// #region Items

	/**
	 * Get the panel at a given column/position
	 * @param column Column number
	 * @param position Position index
	 * @returns Panel object
	 */
	function getItem(column: number, position: number) {
		return dashboard.value?.items.find(item => item.column === column && item.position === position)
	}

	/**
	 * Add a new panel
	 * @param type Type of panel: MYCALLS, EXTENSIONS...
	 * @param column Column number to add it to
	 */
	function addItem(type: PbxAppDashboardItemType, column: number) {
		if (!dashboard.value) { return }

		const baseItem: PbxAppDashboardItemCommon = {
			column: column,
			position: getItemsInColumn(column),
			type: type,
			tag: randomString(64)
		}

		switch (type) {
			case PbxAppDashboardItemType.MYCALLS: {
				const item = baseItem as PbxAppDashboardItemMyCalls

				item.maxCalls = 2
				item.variable = ''
				item.variableName = ''
				dashboard.value.items.push(item)
				break
			}

			case PbxAppDashboardItemType.QUEUE: {
				const item = baseItem as PbxAppDashboardItemQueue

				item._id = ''
				item.variable = ''
				item.variableName = ''
				item.showCalls = true
				item.showMembers = false
				item.showStats = false
				item.maxCalls = 5
				item.maxMembers = 5
				item.stats = [
					PbxAppDashboardItemQueueStats.UNANSWERED,
					PbxAppDashboardItemQueueStats.ANSWERED,
					PbxAppDashboardItemQueueStats.AVERAGEWAITTIME,
					PbxAppDashboardItemQueueStats.LASTWAITTIME
				]
				dashboard.value.items.push(item)
				break
			}

			case PbxAppDashboardItemType.MULTIQUEUE: {
				const item = baseItem as PbxAppDashboardItemMultiQueue

				item.title = i18n.t('multiQueue.newMultiQueue')
				item._ids = []
				item.variable = ''
				item.variableName = ''
				item.showCalls = true
				item.showMembers = false
				item.showStats = false
				item.maxCalls = 5
				item.maxMembers = 5
				item.stats = [
					PbxAppDashboardItemQueueStats.UNANSWERED,
					PbxAppDashboardItemQueueStats.ANSWERED,
					PbxAppDashboardItemQueueStats.AVERAGEWAITTIME,
					PbxAppDashboardItemQueueStats.LASTWAITTIME
				]
				dashboard.value.items.push(item)
				break
			}

			case PbxAppDashboardItemType.EXTENSIONS: {
				const item = baseItem as PbxAppDashboardItemExtensions

				item.title = i18n.t('dashboards.newExtensionsTitle')
				item.extensions = []
				dashboard.value.items.push(item)
				break
			}

			case PbxAppDashboardItemType.WEB: {
				const item = baseItem as PbxAppDashboardItemWeb

				item.width = 390
				item.height = 400
				item.title = i18n.t('dashboards.newWebTitle')
				item.mode = PbxAppDashboardItemWebMode.STATIC
				item.interval = 0
				item.url = ''
				item.close = PbxAppDashboardItemWebClose.NEVER
				item.ignoreLocal = false
				dashboard.value.items.push(item)
				break
			}

			case PbxAppDashboardItemType.SWITCHVAR: {
				const item = baseItem as PbxAppDashboardItemSwitchVar

				item.title = i18n.t('dashboards.newSwitchvarTitle')
				item.variables = []
				dashboard.value.items.push(item)
				break
			}

			case PbxAppDashboardItemType.SHORTCUTS: {
				const item = baseItem as PbxAppDashboardItemShortcuts

				item.title = i18n.t('dashboards.newShortcutsTitle')
				item.shortcuts = []
				dashboard.value.items.push(item)
				break
			}
		}
	}

	/**
	 * Delete a panel
	 * @param column Column panel is in
	 * @param position Position panel is at
	 */
	function removeItem(column: number, position: number) {
		if (!dashboard.value) { return }
		const index = dashboard.value.items.findIndex(item => item.column === column && item.position === position)

		if (index !== -1) {
			dashboard.value?.items.splice(index, 1) // remove it

			for (const item of dashboard.value.items) { // Move all items below it one up
				if (item.column === column && item.position > position) {
					item.position--
				}
			}
		}

		// Check if we have an empty column but have content in subsequent columns
		const cc = columnCount.value

		for (let column = 0; column < cc; column++) {
			if (getItemsInColumn(column) === 0 && getItemsInColumn(column + 1) > 0) {
				// Move all subsequent columns 1 column left
				for (const item of dashboard.value.items) {
					if (item.column > column) { item.column = item.column - 1 }
				}
				column-- // NOSONAR
			}
		}
	}

	// #endregion

	// #region Drag'n'drop

	/**
	 * Drag and drop completed - now move it for real
	 */
	function dragDropped() {
		if (!dashboard.value) { return }

		if (dragItem.value && dragTarget.value) {
			const oldColumn = dragItem.value.column
			const oldPosition = dragItem.value.position
			const newColumn = dragTarget.value.column
			const newPosition = dragTarget.value.position === -1 ? getColumn(newColumn).length : dragTarget.value.position

			// Remove it from its current position (move positions below it one up)
			for (const item of dashboard.value.items) {
				if (item.column === oldColumn && item.position > oldPosition) {
					item.position--
				}
			}

			// Move target and below one down
			for (const item of dashboard.value.items) {
				if (item.column === newColumn && item.position >= newPosition) {
					item.position++
				}
			}

			// Assign new column/position to the item
			dragItem.value.column = newColumn
			dragItem.value.position = newPosition

			// And clean up
			dragItem.value = undefined
			dragTarget.value = undefined

			// Check if we have an empty column but have content in subsequent columns
			const cc = columnCount

			for (let column = 0; column < cc.value; column++) {
				if (getItemsInColumn(column) === 0 && getItemsInColumn(column + 1) > 0) {
					// Move all subsequent columns 1 column down
					for (const item of dashboard.value.items) {
						if (item.column > column) { item.column = item.column - 1 }
					}
					column-- // NOSONAR
				}
			}
		}
	}

	// #endregion

	// #region Topbar

	/** Do we have items that wish to be displayed in the topbar  */
	const useTopbar = computed(() => dashboards.value.some(dashboard => dashboard.topbar > 0))

	/** Get the list of items to display in the topbar { _id, label, index } */
	const labelsForTopbar = computed(() => {
		const labels = dashboards.value.filter(dashboard => dashboard.topbar > 0).map(o => { return { _id: o._id, label: o.name, index: o.topbar } as DashboardTopbarItem })

		labels.sort(dynamicSort('index'))
		return labels
	})

	/** Get list of groups for create/edit dashboard that is shared */
	async function getGroupsForDashboardEdit() {
		try {
			const res = await api.get<PbxGroupListResponse>('/pbx/group?limit=100')
			const out: DashboardGroupItem[] = []

			out.push({ _id: undefined, text: i18n.t('actionbar.forMembersOfAny') })

			for (const group of res.data.groups) {
				out.push({ _id: group._id, text: group.name })
			}
			return out
		}
		catch (err) {
			globalEvents.emit('CommonErrorModal', { header: i18n.t('dashboards.unexpectedErrorHeader'), message: `${i18n.t('dashboards.unexpectedErrorMessage')} ${(err as AxiosError).message}` })
			return []
		}
	}

	return { dashboards, dashboard, ready, editMode, mayEdit, pbxGroups, mode, useTopbar, labelsForTopbar, dragItemHeight, columnIndexes, columnCount, dragTarget, dragItem,
		loadList, load, save, remove, select, create, edit, cancel, getItemsInColumn, getColumn, getColumnWidth, getItem, addItem, removeItem, dragDropped, emergencyDeleteItem, getGroupsForDashboardEdit }
})
